import {
  Popup,
  TPopupComponent,
} from '../../../../../../../../../shared/components/popup/popup.abstract.tsx';
import { TPopupDefaultActions } from '../../../../../../../../../shared/components/popup/services/popup.service.ts';
import { FC, useMemo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { styled, Button, Stack, Alert } from '@mui/material';
import { FormTextEditor, FormImagePicker } from '../../../../../../../../../shared/components/form';
import { useForm, FormProvider, useFormContext } from 'react-hook-form';
import { fontHelper } from '../../../../../../../../../shared/helpers/font/font.helper.ts';
import { useBeforeClosePopup } from '../../../../../../../../../shared/components/popup/hooks/useBeforeClosePopup.tsx';
import { ReportsConfigurationRes } from '../../../../../../services/printForms.service.ts';
import { reportsConfigurationTabState } from '../../../../../../states/reportsConfiguration.state.ts';

export class GeneralSettingsPopup extends Popup<IGeneralSettingsPopup> {
  Component: FC<TPopupComponent>;
  constructor() {
    super();
    const stream = this.innerStream;
    this.Component = () => {
      const { t } = useTranslation();
      const { getValues: generalFormGetValues } = useFormContext();
      const formMethods = useForm<TForm>({
        defaultValues: {
          [PopupType.HEADER_TEXT]: null,
          [PopupType.LOGO]: '',
          [PopupType.FOOTER_TEXT]: null,
        },
      });
      const {
        reset,
        watch,
        formState: { isDirty },
      } = formMethods;

      const defaultFontSize = useMemo(() => fontHelper.getDefaultFont(['fontSize']), []);
      const [popupType, setPopupType] = useState<PopupType>(PopupType.HEADER_TEXT);
      const SWrapper = useMemo(() => {
        return styled(this.Wrapper)(() => ({
          '& > div > div': { width: '100%', maxWidth: popupType === PopupType.LOGO ? 400 : 480 },
        }));
      }, [popupType]);
      useEffect(() => {
        stream.actionListener('open').subscribe(({ popupType }) => {
          setPopupType(popupType);
        });
      }, []);

      const determinateWarningMessage = (
        generalFormValues: Pick<
          ReportsConfigurationRes,
          | 'articlePricelist'
          | 'offer'
          | 'deliveryNoteA4'
          | 'deliveryNoteA5'
          | 'invoice'
          | 'debtReminder'
        >,
        popupType: PopupType,
      ): boolean => {
        const result: boolean = false;
        for (const field in generalFormValues) {
          const fieldData = generalFormValues?.[
            field as keyof typeof generalFormValues
          ] as unknown as IFieldData;
          if (popupType === PopupType.LOGO) {
            const isLogo = fieldData?.[popupType]?.image;
            const isLogo1Page = fieldData?.logo1Page?.image;
            const isLogoFrom2ndPage = fieldData?.logoFrom2ndPage?.image;
            if (isLogo || isLogo1Page || isLogoFrom2ndPage) {
              return true;
            }
          } else {
            const isFormattedText = fieldData?.[popupType]?.formattedText;
            if (isFormattedText) {
              return true;
            }
          }
        }
        return result;
      };

      const generalFormValues = useMemo(() => {
        const {
          articlePricelist,
          offer,
          deliveryNoteA4,
          deliveryNoteA5,
          invoice,
          debtReminder,
          subCompanyId,
        } = generalFormGetValues() as ReportsConfigurationRes;
        return {
          articlePricelist,
          offer,
          deliveryNoteA4,
          deliveryNoteA5,
          invoice,
          debtReminder,
          subCompanyId,
        };
      }, [generalFormGetValues()]);

      const handleSubmit = (values: TForm) => {
        const dataToSave: Record<string, any> = {};
        for (const formValue in generalFormValues) {
          if (formValue === 'subCompanyId') {
            dataToSave[formValue] = generalFormValues[formValue as keyof typeof generalFormValues];
            continue;
          }
          const formValueTyped = generalFormValues[
            formValue as keyof typeof generalFormValues
          ]! as object;
          if (popupType === PopupType.HEADER_TEXT || popupType === PopupType.FOOTER_TEXT) {
            if (Object.hasOwn(formValueTyped, popupType)) {
              dataToSave[formValue] = { [popupType]: { formattedText: values[popupType] } };
            }
          }
          if (popupType === PopupType.LOGO) {
            if (Object.hasOwn(formValueTyped, popupType)) {
              dataToSave[formValue] = {
                ...dataToSave[formValue],
                [popupType]: { image: values[popupType] },
              };
            }
            if (Object.hasOwn(formValueTyped, 'logo1Page')) {
              dataToSave[formValue] = {
                ...dataToSave[formValue],
                logo1Page: { image: values[popupType] },
              };
            }
            if (Object.hasOwn(formValueTyped, 'logoFrom2ndPage')) {
              dataToSave[formValue] = {
                ...dataToSave[formValue],
                logoFrom2ndPage: { image: values[popupType] },
              };
            }
          }
        }
        reportsConfigurationTabState.pub.saveData(dataToSave);
        reset();
        stream.emit('close');
      };

      useBeforeClosePopup({
        stream,
        isDirty,
        handlers: reset,
      });

      return (
        <SWrapper {...{ stream }} popupTitle={t(`settings.apply_${popupType}_to_all_print_forms`)}>
          <FormProvider {...formMethods}>
            <form noValidate onSubmit={formMethods.handleSubmit(handleSubmit)}>
              {popupType === PopupType.LOGO ? (
                <Stack
                  sx={{
                    alignItems: 'center',
                  }}
                >
                  <FormImagePicker name='logo' maxImgHeight={500} maxImgWidth={500} />
                </Stack>
              ) : (
                <FormTextEditor
                  toolbarItems={['bold', 'italic', 'underline', 'size', 'color', 'alignment']}
                  name={
                    popupType === PopupType.HEADER_TEXT
                      ? PopupType.HEADER_TEXT
                      : PopupType.FOOTER_TEXT
                  }
                  defaultFormat={defaultFontSize}
                  noWrapText
                />
              )}

              {determinateWarningMessage(generalFormValues, popupType) && (
                <Alert severity='warning' sx={{ mt: 2 }}>
                  {t('settings.apply_settings_for_all_print_forms_warning', {
                    field: t(`settings.${popupType}`),
                  })}
                </Alert>
              )}
              <Stack
                direction='row'
                sx={{
                  justifyContent: 'flex-end',
                  mt: 6,
                }}
              >
                <Button disabled={!watch(popupType)} type='submit' variant='contained'>
                  {t('common.save')}
                </Button>
              </Stack>
            </form>
          </FormProvider>
        </SWrapper>
      );
    };
  }
}

interface IGeneralSettingsPopup {
  action: TPopupDefaultActions;
  popupType: PopupType;
}

export enum PopupType {
  HEADER_TEXT = 'letterhead',
  LOGO = 'logo',
  FOOTER_TEXT = 'footerText',
}

type TForm = {
  [key in PopupType]: string | null;
};

interface IFieldData {
  [PopupType.LOGO]: ISubFields;
  [PopupType.HEADER_TEXT]: ISubFields;
  [PopupType.FOOTER_TEXT]: ISubFields;
  logo1Page: ISubFields;
  logoFrom2ndPage: ISubFields;
}

interface ISubFields {
  formattedText?: string | null;
  image?: string | null;
}
